import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { DocumentViewer } from '@dealstack/frontend/shared/components/DocumentViewer/DocumentViewer';
import { difference, isEmpty } from 'lodash';
import {
	collectCustomDetailDefinitionsIdsFromSourceTemplate,
	PreFillableTemplateFieldShape,
} from '@dealstack/frontend/admin/pages/Manage/ManagePreFillableForms/common/utils.ts';
import { EditFieldMappingModal } from '@dealstack/frontend/admin/pages/Manage/common/EditFieldMappingModal/EditFieldMappingModal.tsx';
import { CustomDetailDefinitionSource } from '@dealstack/frontend/admin/pages/Manage/common/EditFieldMappingModal/availableSources.ts';

interface Props {
	fileBlob: Blob;
	fillableFields: PreFillableTemplateFieldShape[];
	customDetailsDefinitionsSources?: CustomDetailDefinitionSource[];
}

const PdfTemplateView = ({ fileBlob, fillableFields, customDetailsDefinitionsSources }: Props) => {
	const documentViewRef = useRef<HTMLDivElement>(null);
	const [documentPageRenderedTrigger, onDocumentPageRendered] = useState([]);
	const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null);

	const validatedFields: (PreFillableTemplateFieldShape & { isValid?: boolean })[] =
		useMemo(() => {
			const customDetailsDefinitionsIds =
				customDetailsDefinitionsSources?.map((d) => d.id) ?? [];

			return fillableFields.map((field) => {
				if (!field.fieldSourceTemplate) {
					return field;
				}

				const customDetailDefinitionsIdsInUse =
					collectCustomDetailDefinitionsIdsFromSourceTemplate(field.fieldSourceTemplate);

				const missingIds = difference(
					customDetailDefinitionsIdsInUse,
					customDetailsDefinitionsIds
				);

				return { ...field, isValid: isEmpty(missingIds) };
			});
		}, [JSON.stringify(fillableFields), customDetailsDefinitionsSources]);

	useEffect(() => {
		if (!documentViewRef.current) {
			return;
		}

		for (const validatedField of validatedFields) {
			const pdfInput = documentViewRef.current.querySelector(
				`input[name="${validatedField.fieldKey}"]`
			) as HTMLInputElement;
			if (!pdfInput) {
				continue;
			}

			pdfInput.value = validatedField.fieldSourceTemplate ?? '';

			if (!validatedField.fieldSourceTemplate) {
				pdfInput.classList.remove('highlighted-pdf-input-invalid');
				pdfInput.classList.remove('highlighted-pdf-input-valid');
				continue;
			}

			if (validatedField.isValid) {
				pdfInput.classList.remove('highlighted-pdf-input-invalid');
				pdfInput.classList.add('highlighted-pdf-input-valid');
			} else {
				pdfInput.classList.remove('highlighted-pdf-input-valid');
				pdfInput.classList.add('highlighted-pdf-input-invalid');
			}
		}
	}, [validatedFields, documentViewRef.current, documentPageRenderedTrigger]);

	const onDocumentClick = (e) => {
		const pdfInputKey = e.target?.name;
		if (!pdfInputKey) {
			return;
		}

		setSelectedFieldKey(pdfInputKey);
	};

	const fillableFieldsKeys = useMemo(() => {
		return fillableFields.map((f) => f.fieldKey);
	}, [fillableFields]);

	return (
		<div>
			<div onClick={onDocumentClick}>
				<DocumentViewer
					ref={documentViewRef}
					pdfLoaderProps={{
						withBorder: true,
						renderForms: true,
						onPageRenderSuccess: () => {
							onDocumentPageRendered([]);
						},
					}}
					file={fileBlob}
					fileExtension="pdf"
				/>
			</div>

			{!!selectedFieldKey && (
				<EditFieldMappingModal
					opened={!!selectedFieldKey}
					onClose={() => setSelectedFieldKey(null)}
					fieldKey={selectedFieldKey}
					fieldPath={`fillableFieldsMapping.${fillableFieldsKeys.indexOf(selectedFieldKey)}`}
					customDetailsDefinitionsSources={customDetailsDefinitionsSources}
				/>
			)}
		</div>
	);
};
const _PdfTemplateViewExport = memo(PdfTemplateView);

export { _PdfTemplateViewExport as PdfTemplateView };
